import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { computed, observable } from 'mobx'
import { type GetUserFn, type WorkspaceMemberCompositeId } from '@/features/iam'
import { User } from '@/features/iam/users/User'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { Permissions } from '@/features/iam/permissions/Permissions'

export type WorkspaceMemberProps = ModelProps<WorkspaceMember>

/**
 * Represents membership of a workspace.
 */
export class WorkspaceMember extends Model<WorkspaceMember> {
  /**
   * Sentinel instance.
   */
  static readonly none = new WorkspaceMember(() => User.none)

  @observable accessor userId: Id = Id.none
  @observable accessor workspaceId: Id = Id.none
  @observable accessor workspace = Workspace.none
  @observable accessor inactive = false
  @observable accessor approvedTermsVersion: string | null = null
  @observable accessor permissions = Permissions.empty()
  @observable accessor dateCreated: Date | undefined = undefined
  @observable accessor lastLogin: Date | null | undefined = undefined

  constructor(private readonly _getUser: GetUserFn) {
    super()
  }

  @computed
  get user() {
    // Falling back to `User.none` since all members should have a backing user
    return this._getUser(this.userId) ?? User.none
  }

  /**
   * Whether the user has a complete profile.
   */
  @computed
  get isProfileComplete() {
    return Boolean(
      this.user.phone && this.user.givenName && this.user.familyName,
    )
  }

  toCompositeId(): WorkspaceMemberCompositeId {
    return {
      userId: Id.assert(this.userId),
      workspaceId: Id.assert(this.workspaceId),
    }
  }
}
